<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import QrcodeVue from "qrcode.vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import QRCheckTutor from "#/assets/icons/check.svg";
import QrScanner from "#/assets/icons/qr_scanner.svg";
import QrPhone from "#/assets/iconsDynamic/qr_phone.vue";
import QRScannerTutor from "#/assets/iconsDynamic/qr_scanner_tutor.vue";
import { useAuth } from "#/composables/useAuth";
import { vMuted } from "#/directives/vMuted";
import { useScreenBackground } from "#/hooks/useScreenBackground/useScreenBackground";
import { useCartStore } from "#/store/cart";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import ButtonDefault from "#/ui/buttons/default/ButtonDefault.vue";
import AnimNum from "#/ui/recountNumber/RecountNumber.vue";

const themeStore = useThemeStore();
const storeCart = useCartStore();
const storeMain = useMainStore();

const router = useRouter();
const auth = useAuth();
const { t } = useTranslation();

const imageSettings = {
	src: themeStore.data?.logo || "",
	width: 60,
	height: 60,
	excavate: true,
};

const timeout = computed(() => themeStore.data?.kiosk_qr_payment_timeout || 60).value;
const timeLeft = ref(timeout);
let timer: ReturnType<typeof setInterval> | undefined;

const formattedTime = computed(() => {
	const minutes = Math.floor(timeLeft.value / 60);
	const seconds = timeLeft.value % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const ttlPercent = computed(() => (timeLeft.value / timeout) * 100);
const priceQR = computed(() => storeCart.paymentCostQR);

const updateTimer = () => {
	timeLeft.value--;

	if (timeLeft.value <= 0) {
		clearInterval(timer);
		resetOrder();
	}
};

const resetOrder = () => {
	storeMain.removeFromLocalStorage(["cart"]);
	auth.clear();
	storeCart.isAdultConfirmed = false;
	sessionStorage.removeItem("is_adult");
	router.replace({ name: "home" }).then(() => router.go(0));
};

onMounted(() => {
	timer = setInterval(updateTimer, 1000);
});

onBeforeUnmount(() => {
	clearInterval(timer);
});

const props = defineProps<{ isKiosk?: boolean }>();

const { mediaUrl, videoMimeType, isImage } = useScreenBackground(() => !!props.isKiosk);
</script>

<template>
	<div class="wrapper_qr_page">
		<img v-if="mediaUrl && isImage" :key="mediaUrl" :src="mediaUrl" alt="" crossorigin="anonymous">
		<video v-else-if="mediaUrl" loop autoplay muted playsinline v-muted crossorigin="anonymous" :key="mediaUrl">
			<source :src="mediaUrl" :type="videoMimeType">
		</video>
		<div class="start_img_black" :class="{ darken: themeStore.data?.kiosk_preloader_darken }"></div>
		<div class="modal_qr">
			<div class="modal_qr_content">
				<div class="wrapper_icon_qr">
					<img :src="QrScanner" width="100" height="100" alt="">
				</div>
				<h1>{{ t(`cart-complete.qr-code.title`) }}</h1>
				<p class="total_order_price">
					<AnimNum :price="priceQR" :symbol="storeMain.config_lib.currency_symbol" />
				</p>

				<div class="ttl_wrapper">
					<div class="ttl_head">
						<span>{{ t(`cart-complete.qr-code.ttl`) }}</span>
						<b>{{ formattedTime }}</b>
					</div>
					<div class="ttl_bar">
						<div class="ttl_fill" :style="{ width: `${ttlPercent}%` }"></div>
					</div>
				</div>

				<div class="hint hint-anim">
					<div class="steps" aria-hidden="true">
						<div class="step is-active" data-i="0">
							<div class="ico">
								<QrPhone />
							</div>
							<div class="txt">{{ t(`cart-complete.qr-code.step-1`) }}</div>
						</div>

						<div class="sep"></div>

						<div class="step" data-i="1">
							<div class="ico">
								<QRScannerTutor />
							</div>
							<span class="txt">{{ t(`cart-complete.qr-code.step-2`) }}</span>
						</div>

						<span class="sep">→</span>

						<div class="step" data-i="2">
							<div class="ico">
								<img :src="QRCheckTutor" width="30" height="30" alt="">
							</div>
							<span class="txt">{{ t(`cart-complete.qr-code.step-3`) }}</span>
						</div>
					</div>
				</div>

				<div class="qr_border">
					<QrcodeVue :value="storeCart.linkQR" :size="200" level="Q" render-as="svg" :image-settings="imageSettings" />
				</div>

				<ButtonDefault :textButtonDefault="t(`cart-complete.qr-code.button.cancel`)" :onClick="resetOrder" class="cancel_order_btn" />
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
  @use './completedQRCode'
</style>
